import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  BarChart3, ChevronDown, ChevronRight, Loader2, TrendingUp, TrendingDown,
  Minus, Shield, ShieldAlert, ShieldOff, Filter, ArrowRight,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIER_CONFIG = {
  high: { label: 'High', color: 'emerald', icon: Shield, desc: 'Persistent across 80%+ scans' },
  medium: { label: 'Medium', color: 'amber', icon: ShieldAlert, desc: 'Seen in 50-79% of scans' },
  low: { label: 'Low', color: 'slate', icon: ShieldOff, desc: 'Seen in <50% of scans (likely noise)' },
};

const TREND_ICON = { increasing: TrendingUp, decreasing: TrendingDown, stable: Minus };

const ScanConfidenceReport = ({ inspectionId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [tierFilter, setTierFilter] = useState('all');

  const fetchData = useCallback(async () => {
    if (!inspectionId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/inspections/${inspectionId}/obd-confidence`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to load confidence data:', err);
    } finally {
      setLoading(false);
    }
  }, [inspectionId]);

  useEffect(() => {
    if (expanded && !data) fetchData();
  }, [expanded, data, fetchData]);

  const filteredCodes = useMemo(() => {
    if (!data?.codes) return [];
    if (tierFilter === 'all') return data.codes;
    return data.codes.filter(c => c.tier === tierFilter);
  }, [data, tierFilter]);

  const nr = data?.noise_reduction || {};
  const summary = data?.summary || {};

  if (!inspectionId) return null;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="scan-confidence-report">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
        data-testid="confidence-report-toggle"
      >
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-slate-600" />
          <span className="font-semibold text-sm text-slate-700">Scan Confidence Report</span>
          {data && (
            <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">
              {data.total_scans} scan{data.total_scans !== 1 ? 's' : ''} · {summary.total_unique_codes || 0} codes
            </span>
          )}
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="p-3 space-y-4 bg-white">
          {loading ? (
            <div className="flex items-center justify-center py-8 gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              <span className="text-sm text-slate-500">Analyzing scan history...</span>
            </div>
          ) : !data || data.total_scans === 0 ? (
            <div className="text-center py-8">
              <BarChart3 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No scan data available</p>
              <p className="text-xs text-slate-400 mt-1">Confidence report requires at least one OBD scan</p>
            </div>
          ) : (
            <>
              {/* ── Summary Stats Row ── */}
              <div className="grid grid-cols-4 gap-2" data-testid="confidence-summary-stats">
                <StatCard label="Total Scans" value={data.total_scans} sub="sessions" color="slate" />
                <StatCard label="Unique Codes" value={summary.total_unique_codes} sub="across all scans" color="blue" />
                <StatCard label="Avg Raw/Scan" value={nr.avg_raw_per_scan} sub="before filtering" color="orange" />
                <StatCard label="High Confidence" value={summary.high_count} sub="persistent faults" color="emerald" />
              </div>

              {/* ── Noise Reduction Funnel ── */}
              <NoiseFunnel nr={nr} summary={summary} totalScans={data.total_scans} />

              {/* ── Tier Distribution ── */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Confidence Tiers</p>
                  <div className="flex gap-1">
                    {['all', 'high', 'medium', 'low'].map(t => (
                      <button
                        key={t}
                        onClick={() => setTierFilter(t)}
                        data-testid={`tier-filter-${t}`}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                          tierFilter === t
                            ? 'bg-slate-800 text-white'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                      >
                        {t === 'all' ? 'All' : TIER_CONFIG[t].label} ({t === 'all' ? summary.total_unique_codes : (data.tiers[t]?.length || 0)})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tier summary bars */}
                <TierBar tiers={data.tiers} total={summary.total_unique_codes} />
              </div>

              {/* ── Per-Code Table ── */}
              {filteredCodes.length > 0 ? (
                <div className="border border-slate-100 rounded-lg overflow-hidden" data-testid="confidence-code-table">
                  <div className="overflow-auto max-h-[400px]">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-slate-500">Code</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-500">Description</th>
                          <th className="text-center px-3 py-2 font-medium text-slate-500">Seen In</th>
                          <th className="text-center px-3 py-2 font-medium text-slate-500">Confidence</th>
                          <th className="text-center px-3 py-2 font-medium text-slate-500">Tier</th>
                          <th className="text-center px-3 py-2 font-medium text-slate-500">Scan Range</th>
                          <th className="text-center px-3 py-2 font-medium text-slate-500">Trend</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {filteredCodes.map((c, i) => {
                          const TrendIcon = TREND_ICON[c.trend] || Minus;
                          const tierCfg = TIER_CONFIG[c.tier] || TIER_CONFIG.low;
                          return (
                            <tr key={c.code} className={i % 2 ? 'bg-slate-50/40' : 'bg-white'} data-testid={`confidence-row-${c.code}`}>
                              <td className="px-3 py-2 font-mono font-bold text-slate-800">{c.code}</td>
                              <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate">{c.description || '—'}</td>
                              <td className="px-3 py-2 text-center">
                                <span className="font-semibold text-slate-700">{c.seen_count}</span>
                                <span className="text-slate-400">/{c.total_scans}</span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <ConfidenceBar pct={c.confidence} tier={c.tier} />
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                                  c.tier === 'high' ? 'bg-emerald-100 text-emerald-700' : c.tier === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                                }`}>
                                  {tierCfg.label}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center text-slate-500">
                                #{c.first_seen_scan}–#{c.last_seen_scan}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <TrendIcon className={`h-3.5 w-3.5 mx-auto ${
                                  c.trend === 'increasing' ? 'text-red-500' : c.trend === 'decreasing' ? 'text-emerald-500' : 'text-slate-400'
                                }`} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-sm text-slate-400">No codes in this tier</div>
              )}

              {/* Refresh */}
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={fetchData} className="text-xs text-slate-500" data-testid="confidence-refresh">
                  <BarChart3 className="h-3 w-3 mr-1" />Refresh
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Sub-Components ── */

const STAT_STYLES = {
  slate: { card: 'border-slate-200 bg-slate-50/50', value: 'text-slate-700' },
  blue: { card: 'border-blue-200 bg-blue-50/50', value: 'text-blue-700' },
  orange: { card: 'border-orange-200 bg-orange-50/50', value: 'text-orange-700' },
  emerald: { card: 'border-emerald-200 bg-emerald-50/50', value: 'text-emerald-700' },
};

const StatCard = ({ label, value, sub, color }) => {
  const s = STAT_STYLES[color] || STAT_STYLES.slate;
  return (
    <div className={`rounded-lg border ${s.card} p-2.5`}>
      <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-bold ${s.value} mt-0.5`}>{value ?? '—'}</p>
      <p className="text-[10px] text-slate-400">{sub}</p>
    </div>
  );
};

const ConfidenceBar = ({ pct, tier }) => {
  const color = tier === 'high' ? 'bg-emerald-500' : tier === 'medium' ? 'bg-amber-500' : 'bg-slate-400';
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-[10px] font-semibold text-slate-600 w-8 text-right">{pct}%</span>
    </div>
  );
};

const TierBar = ({ tiers, total }) => {
  if (!total || total === 0) return null;
  const high = tiers?.high?.length || 0;
  const med = tiers?.medium?.length || 0;
  const low = tiers?.low?.length || 0;
  return (
    <div className="flex h-3 rounded-full overflow-hidden bg-slate-100" data-testid="tier-distribution-bar">
      {high > 0 && <div className="bg-emerald-500 transition-all" style={{ width: `${(high / total) * 100}%` }} title={`High: ${high}`} />}
      {med > 0 && <div className="bg-amber-400 transition-all" style={{ width: `${(med / total) * 100}%` }} title={`Medium: ${med}`} />}
      {low > 0 && <div className="bg-slate-300 transition-all" style={{ width: `${(low / total) * 100}%` }} title={`Low: ${low}`} />}
    </div>
  );
};

const NoiseFunnel = ({ nr, summary, totalScans }) => {
  if (!totalScans) return null;

  const steps = [
    { label: 'Avg Raw / Scan', value: nr.avg_raw_per_scan || 0, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
    { label: 'Unique Across Scans', value: summary.total_unique_codes || 0, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
    { label: 'High Confidence', value: summary.high_count || 0, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
    { label: 'AI Verified', value: nr.ai_final || 0, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  ];

  return (
    <div className="space-y-1.5" data-testid="noise-reduction-funnel">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Noise Reduction Pipeline</p>
      <div className="flex items-center gap-1">
        {steps.map((step, i) => (
          <React.Fragment key={step.label}>
            <div className={`flex-1 rounded-lg border ${step.border} ${step.bg} p-2 text-center`}>
              <p className={`text-lg font-bold ${step.color}`}>{step.value}</p>
              <p className="text-[9px] text-slate-500 leading-tight mt-0.5">{step.label}</p>
            </div>
            {i < steps.length - 1 && (
              <ArrowRight className="h-3.5 w-3.5 text-slate-300 flex-shrink-0" />
            )}
          </React.Fragment>
        ))}
      </div>
      {nr.avg_raw_per_scan > 0 && (nr.ai_final > 0 || summary.high_count > 0) && (
        <p className="text-[10px] text-slate-400 text-right">
          {Math.round(((nr.avg_raw_per_scan - (nr.ai_final || summary.high_count || 0)) / nr.avg_raw_per_scan) * 100)}% noise eliminated
        </p>
      )}
    </div>
  );
};

export default ScanConfidenceReport;
